/**
 * Bridge AI - Computer player logic
 * Handles bidding and play decisions for AI opponents/partners
 */

import {
  calculateHCP,
  calculateDistributionPoints,
  getCardsOfSuit,
  SUITS,
  BID_STRAINS,
  SPECIAL_BIDS
} from './bridgeConstants';

// AI difficulty levels
export const AI_LEVELS = {
  BEGINNER: 'beginner',
  INTERMEDIATE: 'intermediate',
  ADVANCED: 'advanced'
};

// Check if a bid is higher than the current contract
function isHigherBid(newBid, currentContract) {
  if (!currentContract) return true;
  if (newBid.level > currentContract.level) return true;
  if (newBid.level === currentContract.level) {
    return BID_STRAINS.indexOf(newBid.strain) > BID_STRAINS.indexOf(currentContract.strain);
  }
  return false;
}

// Get the current contract from auction
function getCurrentContract(auction) {
  for (let i = auction.length - 1; i >= 0; i--) {
    const bid = auction[i].bid;
    if (bid !== SPECIAL_BIDS.PASS && bid !== SPECIAL_BIDS.DOUBLE && bid !== SPECIAL_BIDS.REDOUBLE) {
      return bid;
    }
  }
  return null;
}

/**
 * AI Bidding Logic - Simplified and more reliable
 */
export function getAIBid(hand, auction, position, partnerBid, aiLevel = AI_LEVELS.INTERMEDIATE) {
  const hcp = calculateHCP(hand);
  const distPoints = calculateDistributionPoints(hand);
  const totalPoints = hcp + distPoints;
  
  // Get current contract
  const currentContract = getCurrentContract(auction);
  
  // Count cards in each suit
  const suitLengths = {
    SPADES: getCardsOfSuit(hand, 'SPADES').length,
    HEARTS: getCardsOfSuit(hand, 'HEARTS').length,
    DIAMONDS: getCardsOfSuit(hand, 'DIAMONDS').length,
    CLUBS: getCardsOfSuit(hand, 'CLUBS').length
  };
  
  // Find longest suit
  let longestSuit = 'CLUBS';
  let longestLength = 0;
  Object.entries(suitLengths).forEach(([suit, length]) => {
    if (length > longestLength) {
      longestLength = length;
      longestSuit = suit;
    }
  });
  
  // Check for major suit fit (5+ cards)
  const hasMajor = suitLengths.SPADES >= 5 || suitLengths.HEARTS >= 5;
  const majorSuit = suitLengths.SPADES >= suitLengths.HEARTS ? 'SPADES' : 'HEARTS';
  
  // Helper to find a valid bid
  const findValidBid = (preferredLevel, preferredStrain) => {
    // Try preferred bid first
    const preferredBid = { level: preferredLevel, strain: preferredStrain };
    if (isHigherBid(preferredBid, currentContract)) {
      return preferredBid;
    }
    
    // If preferred bid isn't valid, find the minimum valid bid
    for (let level = 1; level <= 7; level++) {
      for (const strain of BID_STRAINS) {
        const bid = { level, strain };
        if (isHigherBid(bid, currentContract)) {
          // Only bid if we have enough points for this level
          const requiredPoints = 10 + (level - 1) * 3;
          if (totalPoints >= requiredPoints) {
            return bid;
          }
          return null; // Can't afford minimum valid bid
        }
      }
    }
    return null;
  };
  
  // Weak hand - always pass
  if (totalPoints < 10) {
    return { bid: SPECIAL_BIDS.PASS };
  }
  
  // Opening or responding
  if (!currentContract) {
    // No bids yet - make opening bid
    if (totalPoints >= 12) {
      if (hasMajor && longestLength >= 5) {
        return { bid: { level: 1, strain: majorSuit } };
      }
      return { bid: { level: 1, strain: longestSuit } };
    }
    return { bid: SPECIAL_BIDS.PASS };
  }
  
  // There's already a contract - decide whether to compete
  const contractLevel = currentContract.level;
  
  // Strong hand - try to outbid
  if (totalPoints >= 16) {
    const bid = findValidBid(contractLevel + 1, longestSuit);
    if (bid && bid.level <= 4) {
      return { bid };
    }
  }
  
  // Medium hand - only compete at low levels
  if (totalPoints >= 12 && contractLevel <= 2) {
    const bid = findValidBid(contractLevel + 1, longestSuit);
    if (bid && bid.level <= 3) {
      return { bid };
    }
  }
  
  // Default: pass
  return { bid: SPECIAL_BIDS.PASS };
}

/**
 * AI Play Logic - Choose which card to play
 */
export function getAIPlay(hand, trick, trumpSuit, contract, trickHistory, position, aiLevel = AI_LEVELS.INTERMEDIATE) {
  // Determine which cards are legal to play
  const legalCards = getLegalCards(hand, trick);
  
  if (legalCards.length === 1) {
    return legalCards[0];
  }
  
  // Leading the trick
  if (trick.length === 0) {
    return chooseLeadCard(legalCards, trumpSuit, contract, trickHistory, aiLevel);
  }
  
  // Following in the trick
  return chooseFollowCard(legalCards, trick, trumpSuit, position, aiLevel);
}

/**
 * Get legal cards that can be played
 */
function getLegalCards(hand, trick) {
  if (trick.length === 0) {
    return hand; // Can lead any card
  }
  
  const leadSuit = trick[0].card.suit;
  const suitCards = hand.filter(card => card.suit === leadSuit);
  
  // Must follow suit if possible
  if (suitCards.length > 0) {
    return suitCards;
  }
  
  // Otherwise can play any card
  return hand;
}

/**
 * Choose which card to lead
 */
function chooseLeadCard(cards, trumpSuit, contract, trickHistory, aiLevel) {
  // Prefer leading from long suits
  const suitCounts = {};
  cards.forEach(card => {
    suitCounts[card.suit] = (suitCounts[card.suit] || 0) + 1;
  });
  
  // Find suits with 4+ cards (long suits)
  const longSuits = Object.entries(suitCounts)
    .filter(([_, count]) => count >= 4)
    .map(([suit]) => suit);
  
  // If we have a long suit, lead 4th best
  if (longSuits.length > 0) {
    const suit = longSuits[0];
    const suitCards = cards.filter(c => c.suit === suit).sort((a, b) => b.value - a.value);
    if (suitCards.length >= 4) {
      return suitCards[3]; // 4th best
    }
    return suitCards[suitCards.length - 1]; // Low from short suit
  }
  
  // Against NT - lead 4th best of longest suit
  if (!trumpSuit || contract?.strain === 'NOTRUMP') {
    const longestSuit = Object.entries(suitCounts)
      .sort((a, b) => b[1] - a[1])[0][0];
    const suitCards = cards.filter(c => c.suit === longestSuit).sort((a, b) => b.value - a.value);
    if (suitCards.length >= 4) {
      return suitCards[3];
    }
    return suitCards[Math.floor(suitCards.length / 2)];
  }
  
  // Against suit contract - avoid leading trumps, lead from sequence
  const nonTrumpCards = cards.filter(c => c.suit !== trumpSuit);
  if (nonTrumpCards.length > 0) {
    // Look for sequence (3+ cards in a row)
    for (const card of nonTrumpCards) {
      const suitCards = nonTrumpCards.filter(c => c.suit === card.suit).sort((a, b) => b.value - a.value);
      if (suitCards.length >= 2 && suitCards[0].value - suitCards[1].value === 1) {
        return suitCards[0]; // Top of sequence
      }
    }
    // Lead low from weak suits
    return nonTrumpCards.sort((a, b) => a.value - b.value)[0];
  }
  
  // Have to lead trump - lead low
  return cards.sort((a, b) => a.value - b.value)[0];
}

/**
 * Choose which card to play when following
 */
function chooseFollowCard(cards, trick, trumpSuit, position, aiLevel) {
  const leadCard = trick[0].card;
  const leadSuit = leadCard.suit;
  const highestInTrick = getHighestCardInTrick(trick, trumpSuit);
  
  // Determine if partner is currently winning
  const partnerPosition = getPartnerPosition(position);
  const partnerWinning = trick.some(t => 
    t.player === partnerPosition && 
    isWinningCard(t.card, highestInTrick, trumpSuit, leadSuit)
  );
  
  // Following suit
  const sameSuitCards = cards.filter(c => c.suit === leadSuit);
  if (sameSuitCards.length > 0) {
    // Partner winning - play low
    if (partnerWinning) {
      return sameSuitCards.sort((a, b) => a.value - b.value)[0];
    }
    
    // Try to win with minimum winning card
    const winningCards = sameSuitCards.filter(c => c.value > highestInTrick.value);
    if (winningCards.length > 0) {
      return winningCards.sort((a, b) => a.value - b.value)[0]; // Lowest winning card
    }
    
    // Can't win - play low
    return sameSuitCards.sort((a, b) => a.value - b.value)[0];
  }
  
  // Can't follow suit - consider trumping
  const trumpCards = trumpSuit ? cards.filter(c => c.suit === trumpSuit) : [];
  
  if (trumpCards.length > 0 && !partnerWinning) {
    // Trump with lowest trump that wins
    const highestTrumpInTrick = trick
      .filter(t => t.card.suit === trumpSuit)
      .sort((a, b) => b.card.value - a.card.value)[0];
    
    if (!highestTrumpInTrick) {
      // No trump played yet - use lowest trump
      return trumpCards.sort((a, b) => a.value - b.value)[0];
    }
    
    // Need to overtrump
    const overtrumps = trumpCards.filter(c => c.value > highestTrumpInTrick.card.value);
    if (overtrumps.length > 0) {
      return overtrumps.sort((a, b) => a.value - b.value)[0];
    }
  }
  
  // Discard - throw lowest card from weakest suit
  const nonTrumpCards = cards.filter(c => c.suit !== trumpSuit);
  if (nonTrumpCards.length > 0) {
    return nonTrumpCards.sort((a, b) => a.value - b.value)[0];
  }
  
  // Only have trumps - play lowest
  return cards.sort((a, b) => a.value - b.value)[0];
}

/**
 * Get the highest card currently in the trick
 */
function getHighestCardInTrick(trick, trumpSuit) {
  const leadSuit = trick[0].card.suit;
  
  // Check for trumps first
  if (trumpSuit) {
    const trumpsPlayed = trick.filter(t => t.card.suit === trumpSuit);
    if (trumpsPlayed.length > 0) {
      return trumpsPlayed.sort((a, b) => b.card.value - a.card.value)[0].card;
    }
  }
  
  // Highest of lead suit
  return trick
    .filter(t => t.card.suit === leadSuit)
    .sort((a, b) => b.card.value - a.card.value)[0].card;
}

/**
 * Check if a card is the winning card
 */
function isWinningCard(card, highestCard, trumpSuit, leadSuit) {
  if (trumpSuit) {
    if (card.suit === trumpSuit && highestCard.suit !== trumpSuit) return true;
    if (highestCard.suit === trumpSuit && card.suit !== trumpSuit) return false;
  }
  
  if (card.suit !== highestCard.suit) return false;
  return card.value >= highestCard.value;
}

/**
 * Get partner's position
 */
function getPartnerPosition(position) {
  const partners = {
    NORTH: 'SOUTH',
    SOUTH: 'NORTH',
    EAST: 'WEST',
    WEST: 'EAST'
  };
  return partners[position];
}

export default {
  AI_LEVELS,
  getAIBid,
  getAIPlay
};
